import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  const { ref: statsRef, inView: statsInView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: featuresRef, inView: featuresInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      title: 'Simple Integration',
      description: 'Connect your business data sources with one-click OAuth authentication.',
      icon: '🔗',
    },
    {
      title: 'Enterprise Security',
      description: 'AES-256 encryption, OAuth 2.0, and zero data retention policies.',
      icon: '🔒',
    },
    {
      title: 'Universal Compatibility',
      description: 'Works with Claude, ChatGPT, and any MCP-compatible AI assistant.',
      icon: '🌐',
    },
    {
      title: 'Real-time Sync',
      description: 'Access live data from Google Analytics, Facebook Ads, and more.',
      icon: '⚡',
    },
  ];

  const connectors = [
    { name: 'Google Analytics', active: true },
    { name: 'Google Search Console', active: true }, 
    { name: 'Facebook Ads', active: true },
    { name: 'Salesforce', active: false },
    { name: 'Slack', active: false },
    { name: 'Stripe', active: false },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 border-b border-gray-300 transition-all duration-200 ${
        isScrolled ? 'nav-sticky' : 'bg-white'
      }`}>
        <div className="max-w-6xl mx-auto px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-black rounded flex items-center justify-center logo-hover">
              <span className="text-white text-xs font-semibold">M</span>
            </div>
            <span className="text-base font-medium">MCP Platform</span>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-black transition-colors duration-200"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="btn-primary"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-12 py-32 pt-40">
        <div className="max-w-3xl">
          <h1 className="text-6xl font-light leading-tight tracking-tight mb-6 fade-in-up">
            Connect your data<br />to AI assistants
          </h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed fade-in-stagger-1">
            The production-ready MCP platform that connects Google Analytics, 
            Facebook Ads, and your business tools to Claude, ChatGPT, and any AI assistant. 
            One-click setup, enterprise security, zero configuration.
          </p>
          <div className="flex gap-4 fade-in-stagger-2">
            <button
              onClick={() => navigate('/register')}
              className="btn-primary"
            >
              Start Free Trial
            </button>
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-gray-300 bg-gray-50" ref={statsRef}>
        <div className="max-w-6xl mx-auto px-12 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center hover-lift">
              <div className="text-3xl font-light mb-1 tabular-nums text-teal-600">
                {statsInView ? (
                  <CountUp end={10000} suffix="+" duration={2.5} />
                ) : (
                  '0+'
                )}
              </div>
              <div className="text-sm text-gray-600">Active Users</div>
            </div>
            <div className="text-center hover-lift">
              <div className="text-3xl font-light mb-1 tabular-nums text-teal-600">
                {statsInView ? (
                  <CountUp end={1000000} suffix="+" duration={2.5} separator="," />
                ) : (
                  '0+'
                )}
              </div>
              <div className="text-sm text-gray-600">API Requests/Day</div>
            </div>
            <div className="text-center hover-lift">
              <div className="text-3xl font-light mb-1 tabular-nums text-teal-600">
                {statsInView ? (
                  <CountUp end={99.9} suffix="%" duration={2.5} decimals={1} />
                ) : (
                  '0%'
                )}
              </div>
              <div className="text-sm text-gray-600">Uptime</div>
            </div>
            <div className="text-center hover-lift">
              <div className="text-3xl font-light mb-1 text-teal-600">
                {statsInView ? '< 100ms' : '0ms'}
              </div>
              <div className="text-sm text-gray-600">Response Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-12 py-24" ref={featuresRef}>
        <h2 className="text-3xl font-light mb-16 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group hover-lift ${
                featuresInView ? `fade-in-stagger-${index + 1}` : 'opacity-0'
              }`}
            >
              <div className="text-2xl mb-3">{feature.icon}</div>
              <h3 className="text-base font-medium mb-2 group-hover:text-teal-600 transition-colors duration-200">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Connectors */}
      <section className="bg-gray-50 border-t border-gray-300">
        <div className="max-w-6xl mx-auto px-12 py-24">
          <h2 className="text-3xl font-light mb-16 text-center">Available Connectors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connectors.map((connector, index) => (
              <div
                key={index}
                className="connector-card"
              >
                <div className="flex items-center justify-center">
                  <span className="text-sm font-medium">{connector.name}</span>
                  {connector.active ? (
                    <span className="status-active"></span>
                  ) : (
                    <>
                      <span className="status-coming"></span>
                      <span className="text-xs text-gray-500 ml-2">Coming Soon</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-12 py-24">
        <div className="text-center">
          <h2 className="text-3xl font-light mb-6">Ready to get started?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Join thousands of users enhancing their AI experience with real-time data.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="btn-primary text-base px-8 py-3"
          >
            Start Your Free Trial
          </button>
          <p className="text-sm text-gray-500 mt-4">
            No credit card required • 14-day free trial
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-300">
        <div className="max-w-6xl mx-auto px-12 py-12">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                <span className="text-white text-xs font-semibold">M</span>
              </div>
              <span className="text-sm text-gray-600">© {new Date().getFullYear()} MCP Platform</span>
            </div>
            <div className="flex gap-8">
              <a href="/privacy" className="text-sm text-gray-600 hover:text-black transition-colors duration-200">
                Privacy
              </a>
              <a href="/terms" className="text-sm text-gray-600 hover:text-black transition-colors duration-200">
                Terms
              </a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-black transition-colors duration-200">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;